import { supabase } from '../lib/supabase';

export const negotiationService = {
  // OPTIMIZED: Reduce data fetching overhead by selecting only necessary fields
  async getAll() {
    const { data, error } = await supabase?.from('negotiations')?.select(`
        id,
        title,
        destination,
        sale_value,
        supplier_cost,
        sale_date,
        payment_status,
        notes,
        created_at,
        updated_at,
        client:clients(id, name, email, cpf),
        receivables(
          id,
          amount,
          due_date,
          status,
          operator,
          payment_method
        )
      `)?.order('created_at', { ascending: false })?.limit(100); // OPTIMIZED: Limit to most recent 100 negotiations

    if (error) {
      console.error('Error fetching negotiations:', error);
      throw error;
    }

    return data;
  },

  async getById(id) {
    const { data, error } = await supabase?.from('negotiations')?.select(`
        *,
        client:clients(id, name, email, phone),
        negotiation_passengers(
          passenger:passengers(*)
        ),
        receivables(
          id,
          amount,
          payment_method,
          due_date,
          operator,
          status,
          received_at
        )
      `)?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  },

  async create(negotiationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Insert negotiation first
    const { data: negotiation, error: negotiationError } = await supabase?.from('negotiations')?.insert({
        client_id: negotiationData?.clientId,
        title: negotiationData?.title,
        destination: negotiationData?.destination,
        sale_value: negotiationData?.saleValue,
        supplier_cost: negotiationData?.supplierCost,
        sale_date: negotiationData?.saleDate,
        payment_status: negotiationData?.paymentStatus || 'pending',
        notes: negotiationData?.notes,
        created_by: user?.id
      })?.select()?.single();

    if (negotiationError) throw negotiationError;

    // Create receivables for each payment
    if (negotiationData?.payments?.length > 0) {
      const receivables = negotiationData?.payments?.map(payment => ({
        negotiation_id: negotiation?.id,
        amount: payment?.amount,
        payment_method: payment?.paymentMethod,
        due_date: payment?.dueDate,
        operator: payment?.operator,
        status: 'pending',
        created_by: user?.id
      }));

      const { error: receivablesError } = await supabase?.from('receivables')?.insert(receivables);

      if (receivablesError) {
        // Rollback negotiation if receivables creation fails
        await supabase?.from('negotiations')?.delete()?.eq('id', negotiation?.id);
        throw receivablesError;
      }
    }

    // Link passengers if provided
    if (negotiationData?.passengerIds?.length > 0) {
      await this.linkPassengers(negotiation?.id, negotiationData?.passengerIds);
    }

    return negotiation;
  },

  async update(id, data) {
    try {
      const updateData = {
        title: data?.title,
        client_id: data?.clientId,
        destination: data?.destination,
        sale_value: data?.saleValue,
        supplier_cost: data?.supplierCost || 0,
        sale_date: data?.saleDate,
        payment_status: data?.paymentStatus,
        notes: data?.notes
      };

      const { data: negotiation, error } = await supabase?.from('negotiations')?.update(updateData)?.eq('id', id)?.select(`
          *,
          client:clients(id, name, email, phone)
        `)?.single();

      if (error) throw error;

      return negotiation;
    } catch (error) {
      console.error('Error updating negotiation:', error);
      throw error;
    }
  },

  async updateReceivables(negotiationId, payments) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Get existing receivables
      const { data: existingReceivables } = await supabase?.from('receivables')?.select('id')?.eq('negotiation_id', negotiationId);

      const existingIds = existingReceivables?.map(r => r?.id) || [];
      const updateIds = payments?.filter(p => p?.id)?.map(p => p?.id) || [];
      
      // Delete receivables that are no longer in the payments list
      const idsToDelete = existingIds?.filter(id => !updateIds?.includes(id));
      if (idsToDelete?.length > 0) {
        await supabase?.from('receivables')?.delete()?.in('id', idsToDelete);
      }

      // Update or insert receivables
      for (const payment of payments) {
        if (payment?.id) {
          // Update existing receivable - NOW INCLUDING STATUS
          await supabase?.from('receivables')?.update({
            amount: payment?.amount,
            payment_method: payment?.paymentMethod,
            due_date: payment?.dueDate,
            operator: payment?.operator,
            status: payment?.paymentStatus // FIXED: Added status field to update
          })?.eq('id', payment?.id);
        } else {
          // Insert new receivable
          await supabase?.from('receivables')?.insert({
            negotiation_id: negotiationId,
            amount: payment?.amount,
            payment_method: payment?.paymentMethod,
            due_date: payment?.dueDate,
            operator: payment?.operator,
            status: payment?.paymentStatus || 'pending', // FIXED: Use provided status or default to pending
            created_by: user?.id
          });
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating receivables:', error);
      throw error;
    }
  },

  async delete(id) {
    const { error } = await supabase?.from('negotiations')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  async linkPassengers(negotiationId, passengerIds) {
    const inserts = passengerIds?.map(passengerId => ({
      negotiation_id: negotiationId,
      passenger_id: passengerId
    }));

    const { error } = await supabase?.from('negotiation_passengers')?.insert(inserts);

    if (error) throw error;
  },

  async updatePassengerLinks(negotiationId, passengerIds) {
    // Remove existing links
    await supabase?.from('negotiation_passengers')?.delete()?.eq('negotiation_id', negotiationId);

    // Add new links
    if (passengerIds?.length > 0) {
      await this.linkPassengers(negotiationId, passengerIds);
    }
  },

  async getFinancialSummary() {
    const { data: negotiations, error } = await supabase?.from('negotiations')?.select('sale_value, supplier_cost, payment_status, sale_date');

    if (error) throw error;

    const summary = {
      totalRevenue: 0,
      totalCost: 0,
      totalProfit: 0,
      pendingRevenue: 0,
      completedRevenue: 0
    };

    negotiations?.forEach(neg => {
      const revenue = parseFloat(neg?.sale_value) || 0;
      const cost = parseFloat(neg?.supplier_cost) || 0;
      
      summary.totalRevenue += revenue;
      summary.totalCost += cost;
      summary.totalProfit += (revenue - cost);

      if (neg?.payment_status === 'paid') {
        summary.completedRevenue += revenue;
      } else {
        summary.pendingRevenue += revenue;
      }
    });

    return summary;
  }
};